import React from 'react';
import { PackagePlusIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { CampaignInterestForm } from './CampaignInterestForm';
const agencyUses = ['PRODUCT LAUNCHES', 'BRAND ACTIVATIONS', 'SAMPLING CAMPAIGNS', 'LOCAL DROPS', 'RETAIL ACTIVATION', 'EVENT CAMPAIGNS', 'AUDIENCE ENGAGEMENT'];
export function Manifesto() {
  const reduceMotion = useReducedMotion();
  return <>
      <section id="about" className="flex min-h-[86vh] items-center bg-white px-5 py-24 md:px-10" aria-label="Sampler brand idea" data-id="element-539">
        <div className="mx-auto w-full max-w-[1280px]" data-id="element-540">
          <TypographyStep text="Advertising buys attention." muted data-id="element-541" />
          <TypographyStep text="Sampling gives away products." muted data-id="element-542" />
          <TypographyStep text="Sampler creates product experiences." accent data-id="element-543" />
          <TypographyStep text="Experiences people choose to join." data-id="element-544" />
          <motion.p initial={reduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-20 font-display text-6xl font-bold leading-[0.8] text-[#ff4d17] md:text-9xl" data-id="element-545">
            THAT’S THE DIFFERENCE.
          </motion.p>
        </div>
      </section>

      <section id="for-agencies" className="border-y border-black/15 bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="agency-heading" data-id="element-546">
        <div className="mx-auto max-w-[1280px]" data-id="element-547">
          <div className="grid gap-8 md:grid-cols-2 md:items-end" data-id="element-548">
            <div data-id="element-549">
              <p className="text-xs font-extrabold tracking-[0.16em] text-black/45" data-id="element-550">
                FOR MEDIA & ACTIVATION PARTNERS
              </p>
              <h2 id="agency-heading" className="mt-5 font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-551">
                Add product experience to the media plan.
              </h2>
            </div>
            <p className="max-w-xl text-lg leading-relaxed text-black/65" data-id="element-552">
              Sampler gives agencies a campaign technology and experience layer
              that connects brand activity with real product experience. It is
              not a staffing or event agency.
            </p>
          </div>
          <div className="mt-14 flex flex-wrap border-y border-black/15 py-6" data-id="element-553">
            {agencyUses.map(use => <span key={use} className="mr-6 py-2 text-sm font-extrabold tracking-[0.08em]" data-id="element-554">
                {use}
              </span>)}
          </div>
          <p className="mt-12 font-display text-4xl font-bold md:text-6xl" data-id="element-555">
            One platform.
            <br data-id="element-556" />
            Multiple brands.
            <br data-id="element-557" />
            <span className="text-[#ff4d17]" data-id="element-558">A new campaign format.</span>
          </p>
        </div>
      </section>

      <section className="bg-white px-5 py-24 md:px-10 md:py-32" aria-labelledby="more-heading" data-id="element-559">
        <div className="mx-auto max-w-[1280px]" data-id="element-560">
          <p className="text-xs font-extrabold tracking-[0.16em] text-black/45" data-id="element-561">
            BUILT FOR MORE
          </p>
          <div className="mt-5 grid gap-10 lg:grid-cols-2 lg:items-end" data-id="element-562">
            <div data-id="element-563">
              <h2 id="more-heading" className="font-display text-5xl font-bold leading-[0.9] md:text-7xl" data-id="element-564">
                Gone Fishing is the first experience.
              </h2>
            </div>
            <p className="text-lg leading-relaxed text-black/65" data-id="element-565">
              Sampler is designed as infrastructure for interactive product
              experiences. Different products. Different audiences. Different
              campaign mechanics. One connected campaign infrastructure.
            </p>
          </div>
          <div className="mt-14 grid gap-4 md:grid-cols-3" data-id="element-566">
            <FutureModule number="01" data-id="element-567" />
            <FutureModule number="02" data-id="element-568" />
            <FutureModule number="03" data-id="element-569" />
          </div>
          <p className="mt-5 text-xs text-black/40" data-id="element-570">
            PLATFORM CONCEPT — future experience infrastructure direction.
          </p>
        </div>
      </section>

      <section className="flex min-h-[82vh] items-center bg-black px-5 py-24 text-white md:px-10" aria-label="Sampler brand statement" data-id="element-571">
        <div className="mx-auto max-w-[1280px]" data-id="element-572">
          <p className="font-display text-[clamp(3.5rem,9vw,9rem)] font-bold leading-[0.82] tracking-[-0.07em]" data-id="element-573">
            DON’T <span className="text-white/45" data-id="element-574">SHOW</span> THEM
            <br data-id="element-575" />
            THE PRODUCT.
          </p>
          <motion.div initial={reduceMotion ? false : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-9 flex items-center gap-5" data-id="element-576">
            <span className="font-display text-4xl font-bold text-white/35 line-through decoration-[#ff4d17] decoration-4 md:text-6xl" data-id="element-577">
              SHOW
            </span>
            <span className="font-display text-4xl font-bold text-[#ff4d17] md:text-6xl" data-id="element-578">
              LET THEM CATCH IT.
            </span>
          </motion.div>
        </div>
      </section>

      <section id="start" className="bg-[#ff4d17] px-5 py-24 text-black md:px-10 md:py-32" aria-labelledby="start-heading" data-id="element-579">
        <div className="mx-auto max-w-[1280px] text-center" data-id="element-580">
          <p className="text-xs font-extrabold tracking-[0.16em] text-black/60" data-id="element-581">
            READY TO DROP SOMETHING?
          </p>
          <h2 id="start-heading" className="mx-auto mt-5 max-w-5xl font-display text-5xl font-bold leading-[0.88] md:text-8xl" data-id="element-582">
            Put your product into the experience.
          </h2>
          <p className="mx-auto mt-7 max-w-xl text-lg leading-relaxed text-black/75" data-id="element-583">
            Tell us what you are planning and we’ll start with the campaign
            context.
          </p>
          <CampaignInterestForm data-id="element-584" />
          <p className="mt-6 text-xs font-bold text-black/60" data-id="element-585">
            For brands, agencies and activation partners.
          </p>
        </div>
      </section>
    </>;
}
function TypographyStep({
  text,
  muted = false,
  accent = false
}: {
  text: string;
  muted?: boolean;
  accent?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  return <motion.p initial={reduceMotion ? false : {
    opacity: 0,
    x: -20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true,
    amount: 0.8
  }} className={`font-display text-[clamp(2.6rem,6vw,6.8rem)] font-bold leading-[0.94] ${muted ? 'text-black/30' : accent ? 'text-[#ff4d17]' : 'text-black'}`} data-id="element-586">
      {text}
    </motion.p>;
}
function FutureModule({
  number
}: {
  number: string;
}) {
  return <div className="relative min-h-[210px] overflow-hidden border border-black/15 p-5" data-id="element-587">
      <p className="text-xs font-bold text-black/40" data-id="element-588">
        EXPERIENCE MODULE / {number}
      </p>
      <div className="absolute bottom-5 left-5 right-5 flex items-end gap-3" data-id="element-589">
        <span className="grid h-14 w-14 place-items-center border border-black/20" data-id="element-590">
          <PackagePlusIcon size={22} data-id="element-591" />
        </span>
        <span className="h-24 flex-1 border border-dashed border-black/20" data-id="element-592" />
      </div>
    </div>;
}